import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCloudArrowUp, faFileLines, faImage, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { ParseErrors } from '../utils/parse-errors'
import UploadFile from '../utils/upload-file'
import UseChaseProvider from '../utils/use-chase-provider'
import clsx from 'clsx'
import { chaseItemDataTypes } from '../utils/data/chase-item-data'
import ToolTip from './tool-tip'

const MAX_FILE_SIZE = 20 * 1024 * 1024

const isImage = (name: string) => /\.(png|jpe?g|gif|webp|heic|heif|bmp)$/i.test(name || "")

const ChaseFileUploader: React.FC = () => {
    const { newFiles, onPushFiles, onUpdateFiles, onDeleteFile, chaseType, setIsUploading, isUploading } = UseChaseProvider()
    const chaseDetails = chaseItemDataTypes.find((chaseItemData) => chaseItemData.id === chaseType)
    const [progress, setProgress] = useState<Record<string, any>>({})
    const [isDragging, setIsDragging] = useState<boolean>(false)
    const [uploadingFiles, setUploadingFiles] = useState<string[]>([])


    const onProgress = (values: Record<string, any>) => {
        setProgress((prev) => ({ ...prev, ...values }))
    }


    const UploadFiles = async (files: FileList | null) => {
        setIsUploading(true)
        try {
            let fileUploadPromise: Promise<{}>[] = []

            for (let fileIdx in files) {
                const file = files[Number(fileIdx)]
                const isFile = newFiles.find((isF) => isF.name === file?.name)

                // skip files over 20 MB, the rest still upload
                if (file?.size > MAX_FILE_SIZE) {
                    toast.error(`${file.name} is too large. The maximum size is 20 MB.`)
                    continue
                }

                if (!isFile && file?.name && file?.type) {
                    onPushFiles({ name: file.name, fileLink: file.name, type: 'files' })
                    setUploadingFiles((prev) => [...prev, file.name])
                    fileUploadPromise.push(new Promise(async (resolve, reject) => {
                        try {
                            const fileResponse = await UploadFile({ file, onProgress: onProgress })
                            resolve(fileResponse)
                        } catch (error) {
                            reject(error)
                        } finally {
                            setUploadingFiles((prev) => prev.filter((name) => name !== file.name))
                        }
                    }))
                }
            }

            const filesPromiseResponse: any = await Promise.all(fileUploadPromise)
            const formatedFilesData = filesPromiseResponse.map((fileRes: any) => fileRes?.status === 200 && fileRes?.data)

            formatedFilesData.map((fileData: any) => {
                onUpdateFiles(fileData)
            })

            setIsUploading(false)
        } catch (error) {
            console.log(error)
            toast.error(ParseErrors(error))
            setIsUploading(false)
        }
    }

    const onFilesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        UploadFiles(e.target.files)
    }

    // ----------------------- [ DRAG AND DROP ] ----------------------------------
    const onDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault()
        !isUploading && setIsDragging(true)
    }

    const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault()
        setIsDragging(false)
        !isUploading && e.dataTransfer?.files?.length && UploadFiles(e.dataTransfer.files)
    }


    console.log(progress)


    return (
        <div className='w-full'>

            <div className='flex items-center justify-between mb-3'>
                <h2 className='text-pill uppercase text-white/[0.66]'>{chaseDetails?.label}</h2>
                <ToolTip />
            </div>

            {/* ------------------------ UPLOAD ZONE -------------------------------- */}

            <div className={clsx('relative gradient-border transition-[transform,opacity] duration-200 ease-out-cubic', isDragging && 'scale-[1.01]', isUploading && 'opacity-60')}>
                <label
                    htmlFor='fileUploader'
                    onDragOver={onDragOver}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={onDrop}
                    aria-disabled={isUploading}
                    className={clsx(
                        'group w-full flex flex-col items-center justify-center text-center px-5 py-7 sm:py-9 rounded-[14.5px] cursor-pointer transition-[background-color] duration-200 ease-out-cubic',
                        isDragging ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]',
                        isUploading && 'pointer-events-none'
                    )}
                >
                    <span className={clsx('w-12 h-12 rounded-tile bg-tile flex items-center justify-center text-white transition-transform duration-300 ease-out-cubic', isDragging ? '-translate-y-1' : 'group-hover:-translate-y-0.5')}>
                        <FontAwesomeIcon icon={faCloudArrowUp} className='text-[20px]' />
                    </span>
                    <span className='mt-3 text-titleSM text-white'>Upload documents</span>
                    <span className='mt-1 text-[14px] text-white/[0.66]'>Take a photo or choose files<span className='hidden sm:inline'>, or drag them here</span></span>
                    <span className='mt-1 text-[12px] text-white/[0.5]'>Max 20 MB per file</span>
                </label>
                <input onChange={onFilesUpload} type="file" id='fileUploader' multiple disabled={isUploading} className='sr-only' />
            </div>

            {/* ------------------------ UPLOAD ZONE -------------------------------- */}


            {/* ------------------------ UPLOADED FILE -------------------------------- */}

            {newFiles?.length > 0 && <ul className='mt-4 flex flex-col gap-2.5'>
                {newFiles?.map((fileData) => {
                    // each row only follows its own upload, not the shared isUploading
                    const isLoading = uploadingFiles.includes(fileData.name)
                    const isDone = progress[fileData.name] === 100 && !isLoading

                    return (
                        <li className='flex items-center gap-3 px-3 py-3 sm:px-4 surface-row animate-drop-in' key={fileData?.fileLink}>
                            <span className={clsx('relative min-w-[44px] h-[44px] rounded-tile flex items-center justify-center transition-colors duration-300', isDone ? 'bg-[rgba(74,222,128,0.14)] text-success' : 'bg-tile text-white')}>
                                <FontAwesomeIcon icon={isImage(fileData?.name) ? faImage : faFileLines} />
                                {isDone && <span className='absolute -right-1 -bottom-1 w-[18px] h-[18px] rounded-full bg-success text-app-bottom ring-2 ring-app-row flex items-center justify-center animate-pop'>
                                    <FontAwesomeIcon icon={faCheck} className='text-[9px]' />
                                </span>}
                            </span>
                            <div className='min-w-0 flex-1'>
                                <h2 className='text-white text-[14px] font-bold truncate'>{fileData?.name}</h2>
                                {isLoading ? <div className='flex items-center gap-2 mt-1.5'>
                                    <div className='flex-1 h-1.5 bg-white/[0.08] rounded-full overflow-hidden'>
                                        <div className={clsx("h-full rounded-full bg-success transition-[width] duration-300 ease-out")} style={{ width: `${progress[fileData.name] || 0}%` }}></div>
                                    </div>
                                    <span className='text-[12px] font-bold text-white/[0.66] tabular-nums min-w-[32px] text-right'>{progress[fileData.name] || 0}%</span>
                                </div> : isDone && <p className='text-[12px] font-semibold mt-0.5 text-success'>Uploaded</p>}
                            </div>
                            {isDone && <button type='button' aria-label={`Remove ${fileData?.name}`} onClick={() => onDeleteFile(fileData.fileLink)} className='w-9 h-9 rounded-full flex items-center justify-center text-white/[0.55] hover:text-alert hover:bg-[rgba(229,57,53,0.16)] active:scale-[0.96] transition-[color,background-color,transform] duration-[120ms] animate-fade-in'>
                                <FontAwesomeIcon icon={faTrashCan} className='text-[14px]' />
                            </button>}
                        </li>
                    )
                })}
            </ul>}

            {/* ------------------------ UPLOADED FILE -------------------------------- */}

        </div>
    )
}

export default ChaseFileUploader
